<script setup lang="ts">
import { computed } from 'vue';
import type { TocLink } from '@nuxt/content';

// Drawer resizing functionality
// Define the props
const props = defineProps({
  tocLinks: {
    type: Array as PropType<TocLink[]>,
    default: () => [], // Default empty array if no tocLinks passed
  },
  drawerWidth: {
    type: Number,
    default: 500,
  },
  rightDrawerOpen: {
    type: Boolean,
    default: true,
  },
  activeTocId: {
    type: String,
    default: '',
  },
});

const rightOriginalWidth = ref(500);
const rightOriginal = ref(500);
const rightDrawerWidth = ref(props.drawerWidth);
const tocIsActive = (id: string) => props.activeTocId === id;

// Function to handle ToC link clicks with smooth scrolling
const handleTocClick = (event: Event, linkId: string) => {
  event.preventDefault();
  const element = document.getElementById(linkId);
  if (element) {
    // Calculate the offset to account for header height and padding
    const headerHeight = 80; // Adjust this based on your header height
    const additionalPadding = 20; // Extra padding for better visibility
    const totalOffset = headerHeight + additionalPadding;
    
    // Get the element's position
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - totalOffset;
    
    // Scroll to the calculated position
    const contentArea = document.querySelector('.content-area');
    if (contentArea) {
      // If we have a content area, scroll within it
      const containerRect = contentArea.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const scrollTop = contentArea.scrollTop + (elementRect.top - containerRect.top) - totalOffset;
      
      contentArea.scrollTo({
        top: scrollTop,
        behavior: 'smooth'
      });
    } else {
      // Fallback to page container
      const pageContainer = document.querySelector('.q-page-container');
      if (pageContainer) {
        const containerRect = pageContainer.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        const scrollTop = pageContainer.scrollTop + (elementRect.top - containerRect.top) - totalOffset;
        
        pageContainer.scrollTo({
          top: scrollTop,
          behavior: 'smooth'
        });
      } else {
        // Final fallback to window scroll
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
    
    // Update the URL hash without triggering a page reload
    window.history.replaceState(null, '', `#${linkId}`);
  }
};

// Function to handle resizing
const handlePanRight = (details: any) => {
  if (details.isFirst) {
    rightOriginalWidth.value = rightDrawerWidth.value;
    rightOriginal.value = details.position.left;
  } else {
    const newDelta = details.position.left - rightOriginal.value;
    const newWidth = Math.max(200, Math.min(800, rightOriginalWidth.value - newDelta));
    rightDrawerWidth.value = newWidth;
  }
};

</script>

<template>
  <q-drawer
    v-if="props.rightDrawerOpen"
    class="sidebar-right full-height"
    show-if-above
    side="right"
    :width="rightDrawerWidth"
  >
    <div class="row fit">
      <div class="sidebar-divider" v-touch-pan.horizontal.prevent.mouse.preserveCursor="handlePanRight">
        <div class="divider-grip"></div>
      </div>

      <q-scroll-area class="fit col sidebar-right full-height">
        <nav>
          <ul>
            <!-- Render the ToC links recursively -->
            <template v-for="(link, index) in props.tocLinks" :key="index">
              <li :class="['toc-item', { 'top-level': link.depth === 2, 'active-toc': tocIsActive(link.id) }]">
                <a 
                  href="#" 
                  @click="handleTocClick($event, link.id)" 
                  class="top-level scifi-font-med"
                  style="cursor: pointer; text-decoration: none;"
                >
                  {{ link.text }}
                </a>
                <ul v-if="link.children && link.children.length > 0">
                  <li v-for="(child, childIndex) in link.children" :key="childIndex" :class="['toc-item', tocIsActive(child.id) ? 'active-toc' : '', { 'toc-level-2': child.depth === 3 }]">
                    <a 
                      href="#" 
                      @click="handleTocClick($event, child.id)" 
                      class="scifi-font-med font-white"
                      style="cursor: pointer; text-decoration: none;"
                    >
                      {{ child.text }}
                    </a>
                    <ul v-if="child.children && child.children.length > 0">
                      <li v-for="(grandChild, grandChildIndex) in child.children" :key="grandChildIndex" :class="['toc-item', 'font-white', tocIsActive(grandChild.id) ? 'active-toc' : '', 'toc-level-3']">
                        <a 
                          href="#" 
                          @click="handleTocClick($event, grandChild.id)" 
                          class="scifi-font-med font-white"
                          style="cursor: pointer; text-decoration: none;"
                        >
                          {{ grandChild.text }}
                        </a>
                      </li>
                    </ul>
                  </li>
                </ul>
              </li>
            </template>
          </ul>
        </nav>
      </q-scroll-area>
    </div>
  </q-drawer>
</template>

<style scoped>
.sidebar-right {
  background-color: #000000;
  color: white;
  padding: 1rem;
  height: 100vh;
  overflow-y: auto;
}

/* Styling for the ToC items */
.toc-item {
  margin-bottom: 10px;
}

.font-white {
  color: white;
}

.toc-level-2,
.toc-level-3 {
  margin-left: 20px;
  color: white !important;
}

.top-level {
  color: orange;
}

.top-level.scifi-font-med {
  color: orange;
}

/* General styling for other NuxtLink elements */
.scifi-font-med {
  font-family: 'Orbitron', sans-serif;
  color: #fff;
  font-size: var(--fs-large);
  letter-spacing: 2px;
  text-shadow: 1px 1px 2px #000;
}

.scifi-font-med:hover {
  text-decoration: underline;
}

/* Active TOC item styling */
.active-toc .scifi-font-med {
  color: green;
  font-weight: bold;
}

.sidebar-divider {
  width: 6px;
  cursor: col-resize;
  background: #0a1418;
  border-left: 1px solid #00ffea20;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.sidebar-divider:hover {
  background: #00ffea10;
  border-left-color: #00ffea40;
}

.divider-grip {
  width: 2px;
  height: 30px;
  background: #00ffea30;
  border-radius: 1px;
}

.sidebar-divider:hover .divider-grip {
  background: #00ffea60;
}
</style>

